import React, { ReactElement } from 'react';
import { Box, Theme } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { ResponsiveContainer } from 'recharts';
import { getChartContainerBackground, CHART_CONTAINER_STYLES } from '../../constants/chartConfig';

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    height: '350px',
    borderRadius: CHART_CONTAINER_STYLES.borderRadius,
    padding: theme.spacing(2),
    position: 'relative',
    overflow: 'hidden',
  },
  responsiveContainer: {
    outline: 'none',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
}));

export interface ChartContainerProps {
  /**
   * Chart height
   * @default '350px'
   */
  height?: string | number;
  
  /**
   * Chart content (Recharts components)
   */
  children: ReactElement;
  
  /**
   * Additional className
   */
  className?: string;
  
  /**
   * Inline styles
   */
  style?: React.CSSProperties;
  
  /**
   * Whether to apply chart container background
   * @default true
   */
  applyBackground?: boolean;
}

/**
 * Reusable chart container component
 * Provides consistent styling and layout for all charts
 */
export const ChartContainer: React.FC<ChartContainerProps> = ({
  height = '350px',
  children,
  className,
  style,
  applyBackground = true,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const containerStyle = {
    ...(applyBackground && {
      background: getChartContainerBackground(isDark),
    }),
    ...style,
  };

  return (
    <Box
      className={`${classes.container} ${className || ''}`}
      style={{ height, ...containerStyle }}
    >
      <ResponsiveContainer
        width="100%"
        height="100%"
        className={classes.responsiveContainer}
        style={containerStyle}
      >
        {children}
      </ResponsiveContainer>
    </Box>
  );
};
